import { useEffect, useMemo } from "react"
import { FlatList, ListRenderItemInfo, StyleSheet, View } from "react-native"
import { useRecoilValue } from "recoil"
import { SearchInput } from "src/components/parts/SearchInput"
import { DrawerStatus } from "src/components/parts/contactNetworkTab/DrawerStatus"
import { MyMatchingOfferIcon } from "src/components/parts/icons/MyMatchingOfferIcon"
import { MatchingListEmpty } from "src/components/parts/matching/MatchingListEmpty"
import { MatchingListFooter, MatchingListFooterMargin } from "src/components/parts/matching/MatchingListFooter"
import { MyMatchingCard } from "src/components/parts/matching/MyMatchingCard"
import { CustomRefreshControl } from "src/components/projects/CustomRefreshControl"
import { Colors } from "src/constants/Colors"
import { MatchingDrawers } from "src/constants/MatchingDrawers"
import { ListActionBackInThread } from "src/constants/OfferDetail"
import { Screens } from "src/constants/Screens"
import { useAsyncSelector } from "src/hooks/useAsyncSelector"
import { useRefresher } from "src/hooks/useRefresher"
import { MainLayout } from "src/layouts/MainLayout"
import { unreadMatchingMessageRequestIdState } from "src/recoil/atoms/matching/unreadMatchingMessageRequestIdState"
import { routeNameState } from "src/recoil/atoms/routeNameState"
import { useMyMatchingOffers } from "src/recoil/hooks/matching/useMyMatchingOffers"
import { useResource } from "src/recoil/hooks/resource/useResource"
import { selectedMyOrganizationSelector } from "src/recoil/selectors/organization/selectedMyOrganizationSelector"
import { OrganizationManagerStackScreenProps } from "src/types.d"
import { MatchingOffersModel } from "src/types/matching/matchingOfferModel"

export const MyMatchingOffer = ({
  navigation,
  route,
}: OrganizationManagerStackScreenProps<typeof MatchingDrawers.MyMatchingOffer>) => {
  const routeName = useRecoilValue(routeNameState)
  const _refresh = useRefresher(unreadMatchingMessageRequestIdState)
  const { value: selectedOrganization } = useAsyncSelector(selectedMyOrganizationSelector)
  const organizationId = useMemo(() => selectedOrganization?.id, [selectedOrganization])
  const { refreshResourceUrl: refreshTeamImageUrl } = useResource({
    type: "matchingTeamImage",
    teamId: organizationId || "",
    size: "thumbnail",
  })
  const {
    myOffers,
    isLoading,
    refreshList,
    setSearchText,
    searchText,
    loadMore,
    hasMore,
    checkCreateNewOffer,
    resetValueCreateOffer,
  } = useMyMatchingOffers()

  const gotoMyOfferDetail = (offerId: string) => {
    navigation.navigate(MatchingDrawers.MatchingOfferDetail, { id: offerId, isMyMatching: true })
  }

  const gotoCreateNewMatching = async () => {
    resetValueCreateOffer()
    checkCreateNewOffer(() => navigation.navigate(Screens.MatchingOfferCreate))
  }
  const ListItem = (listRenderItemInfo: ListRenderItemInfo<MatchingOffersModel>) => {
    const { item } = listRenderItemInfo
    return (
      <View style={styles.contentWrapper}>
        <MyMatchingCard
          to={ListActionBackInThread.myMatching}
          checkIsExpiredOrDeleted={true}
          data={item}
          onPress={() => gotoMyOfferDetail(item.id)}
        />
      </View>
    )
  }

  useEffect(() => {
    if (routeName === MatchingDrawers.MyMatchingOffer) {
      refreshList("")
      _refresh()
      refreshTeamImageUrl()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeName])

  return (
    <MainLayout>
      <View style={[styles.container]}>
        <DrawerStatus Icon={<MyMatchingOfferIcon />} label="募集一覧" />
        <SearchInput value={searchText} onChangeText={setSearchText} onBlur={() => refreshList(searchText)} />
        <FlatList
          data={myOffers}
          renderItem={ListItem}
          ListEmptyComponent={!isLoading ? <MatchingListEmpty label="表示できる募集がありません。" /> : undefined}
          ListFooterComponent={MatchingListFooterMargin}
          refreshing={isLoading}
          refreshControl={
            <CustomRefreshControl
              refreshing={isLoading}
              onRefresh={() => {
                refreshList()
                _refresh()
              }}
            />
          }
          onEndReached={!isLoading && hasMore ? () => loadMore() : undefined}
          keyExtractor={(item) => item.id}
        />

        <MatchingListFooter
          onPress={gotoCreateNewMatching}
          closeCallback={() => navigation.navigate(MatchingDrawers.OtherMatchingOffer)}
        />
      </View>
    </MainLayout>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
  },
  containerLarge: {
    flex: 1,
    backgroundColor: Colors.white,
    paddingLeft: 10,
    paddingRight: 10,
  },
  header: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: Colors.white,
    height: 50,
  },
  headerTitle: {
    fontSize: 15,
    fontWeight: "bold",
    color: Colors.greyshBrown,
    marginLeft: 8,
  },
  contentWrapper: {
    paddingHorizontal: 16,
    paddingTop: 10,
  },
})
